/** @mentions in a comment (pure): the query being typed at the caret, inserting a picked member, and which
 * members a text still mentions (a deleted "@Name" no longer mentions anyone). */

export interface Member {
  user_id: string | null;
  name: string;
  email?: string | null;
}

/** The "@query" that ends at the caret, if the caret is in one ("@" at the start or after a space). */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  if (/[\n@]/.test(query) || query.length > 40) return null;
  return { start: at, query };
}

/** The "@query" the suggestion list is open for: none for an "@" whose list was closed (`closedAt`: Escape, or a
 * member picked there). A picked "@Name " still reads as a query ("Name " matches its member), so without this the
 * list stayed open over the Post button (QA round 2, A-editor). */
export function openMention(text: string, caret: number, closedAt: number | null): { start: number; query: string } | null {
  const at = mentionQuery(text, caret);
  return at && at.start !== closedAt ? at : null;
}

/** Members whose name or email starts a word with the query (best first, at most `limit`). */
export function filterMembers(members: Member[], query: string, limit = 6): Member[] {
  const q = query.trim().toLowerCase();
  if (!q) return members.slice(0, limit);
  const score = (m: Member) => {
    const name = m.name.toLowerCase();
    const email = (m.email ?? "").toLowerCase();
    if (name.startsWith(q)) return 0;
    if (name.split(/\s+/).some((w) => w.startsWith(q))) return 1;
    if (email.startsWith(q)) return 2;
    return name.includes(q) || email.includes(q) ? 3 : -1;
  };
  return members
    .map((m) => ({ m, s: score(m) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => a.s - b.s || a.m.name.localeCompare(b.m.name))
    .slice(0, limit)
    .map((x) => x.m);
}

/** The text with "@query" replaced by "@Name " and the caret after it. */
export function insertMention(text: string, at: { start: number; query: string }, member: Member): { text: string; caret: number } {
  const token = `@${member.name} `;
  const end = at.start + 1 + at.query.length;
  const next = text.slice(0, at.start) + token + text.slice(end);
  return { text: next, caret: at.start + token.length };
}

/** The picked members whose "@Name" is still in the text. */
export function mentionedIds(text: string, picked: Member[]): string[] {
  const out: string[] = [];
  for (const m of picked) if (m.user_id && text.includes(`@${m.name}`) && !out.includes(m.user_id)) out.push(m.user_id);
  return out;
}

/** The text split into plain parts and mention parts, for highlighting. */
export function splitMentions(text: string, names: string[]): { text: string; mention: boolean }[] {
  const sorted = [...new Set(names)].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!sorted.length) return [{ text, mention: false }];
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(?:${escaped.join("|")})`, "g");
  const out: { text: string; mention: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index! > last) out.push({ text: text.slice(last, m.index), mention: false });
    out.push({ text: m[0], mention: true });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), mention: false });
  return out;
}
